import cn from "classnames";
import React, { useRef, useState } from "react";
import ArrowLeftRegular from "ui-v2/icons/ArrowLeftRegular";
import CrossRegular from "ui-v2/icons/CrossRegular";
import SearchRegular from "ui-v2/icons/SearchRegular";
import { Input } from "../Input";
import s from "./SearchBar.module.scss";

enum InputModes {
  text = "text",
  numeric = "numeric",
  email = "email",
  date = "date",
  search = "search",
  tel = "tel",
}

enum Modes {
  light = "light",
  dark = "dark",
}

enum Variants {
  large = "large",
  small = "small",
}

type InputTypes = keyof typeof InputModes;
type ModeTypes = keyof typeof Modes;
type variantTypes = keyof typeof Variants;

interface SearchBarPropTypes extends React.HTMLProps<HTMLInputElement> {
  mode?: ModeTypes | String;
  variant?: variantTypes;
  placeholder?: string;
  isDisabled?: boolean;
  autoFocus?: boolean;
  inputType?: InputTypes;
  value?: string;
  withShadow?: boolean;
  withBorder?: boolean;
  onChange: (e) => void;
  leftIcon?: {
    show?: boolean;
    onClick?: () => void;
    icon?: React.JSX.Element;
  };
  rightIcon?: {
    icon: React.JSX.Element;
    show: boolean;
    onClick?: () => void;
  };
  onCrossClick?: () => void;
};

/**
 * SearchBar
 *
 * @param mode - mode can be dark or light.
 * @param variant - there are two variants like small and large
 * @param placeholder - placeholder text for input value.
 * @param isDisabled - decides whether the input field is disabled or not.
 * @param autoFocus - for autofocus on the input element.
 * @param inputType - input type of input tag like tel, text, number etc.
 * @param withShadow - decides whether to show search bar with shadow or not.
 * @param withBorder - decides whether to show border or not.
 * @param value - value of input tag.
 * @param onChange - function to call when value of input tag changes.
 * @param leftIcon - to show whether to display back icon on left or not and What happens on click of it.
 * @param rightIcon - to show right icon means to display mic button.
 * @param onCrossClick - function which fire on click of cross on right side.
 *
 * @returns
 */
export function SearchBar({
  mode = Modes.dark,
  withBorder = true,
  variant = Variants.large,
  placeholder = "",
  isDisabled = false,
  autoFocus = false,
  inputType = InputModes.text,
  withShadow = false,
  value,
  onChange,
  leftIcon = {
    icon: <SearchRegular width={18} height={18} color="tertiary" />,
    show: false,
    onClick: () => { },
  },
  rightIcon = null,
  onCrossClick,
  ...propsToFwd
}: SearchBarPropTypes) {
  const inputRef = useRef(null);
  const [isFocus, setIsFocus] = useState(false);

  return mode === Modes.light ? (
    <div className={s.light}>
      <Input
        inputType={inputType}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onFocus={() => setIsFocus(true)}
        leftIcon={
          leftIcon?.show && (
            <div
              onClick={leftIcon?.onClick ? leftIcon.onClick : () => { }}
              className={s.leftIcon}
            >
              {leftIcon?.icon}
            </div>
          )
        }
        rightIcon={
          value?.length > 0 &&
            (variant === Variants.small || !rightIcon?.show) ? (
            <div onClick={onCrossClick} className={s.crossIcon}>
              <CrossRegular width={20} height={20} color="tertiary" />
            </div>
          ) : (
            <div className={s.rightIcon}>
              {value?.length > 0 && (
                <div className={s.cross} onClick={onCrossClick}>
                  <CrossRegular width={20} height={20} color="tertiary" />
                </div>
              )}
              {rightIcon?.show && variant === Variants.large && (
                <div className={s.mic} onClick={rightIcon.onClick}>
                  {rightIcon?.icon}
                </div>
              )}
            </div>
          )
        }
        isDisabled={isDisabled}
        value={value}
        onChange={onChange}
        inputClass={cn(s.searchInput, {
          [s.borderColor]: withBorder,
          [s.borderNone]: !withBorder,
          [s.morePadding]: variant === Variants.large,
          [s.shadow]: withShadow,
        })}
        inputRef={inputRef}
        {...propsToFwd}
      />
    </div>
  ) : (
    <div className={s.dark}>
      <Input
        inputType={inputType}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onFocus={() => setIsFocus(true)}
        leftIcon={
          isFocus ? (
            leftIcon?.show && (
              <div
                onClick={leftIcon?.onClick ? leftIcon.onClick : () => { }}
                className={s.leftIcon}
              >
                <ArrowLeftRegular width={18} height={18} color="tertiary" />
              </div>
            )
          ) : (
            <SearchRegular width={18} height={18} color="tertiary" />
          )
        }
        rightIcon={
          value?.length > 0 &&
            (variant === Variants.small || !rightIcon?.show) ? (
            <div onClick={onCrossClick} className={s.crossIcon}>
              <CrossRegular width={20} height={20} color="tertiary" />
            </div>
          ) : (
            <div className={s.rightIcon}>
              {value?.length > 0 && (
                <div className={s.cross} onClick={onCrossClick}>
                  <CrossRegular width={20} height={20} color="tertiary" />
                </div>
              )}
              {rightIcon?.show && variant === Variants.large && (
                <div className={s.mic} onClick={rightIcon.onClick}>
                  {rightIcon?.icon}
                </div>
              )}
            </div>
          )
        }
        isDisabled={isDisabled}
        value={value}
        onChange={onChange}
        inputClass={cn(s.searchInput, {
          [s.borderColor]: withBorder,
          [s.borderNone]: !withBorder,
          [s.morePadding]: variant === Variants.large,
          [s.shadow]: withShadow,
        })}
        inputRef={inputRef}
        {...propsToFwd}
      />
    </div>
  );
}
